import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FloLogo from "@/components/FloLogo";
import { LogOut, Users, Settings, LayoutDashboard, ClipboardList } from "lucide-react";

const NAV = [
  { to: "/admin", end: true, label: "Overview", icon: LayoutDashboard, testid: "admin-nav-overview" },
  { to: "/admin/users", end: false, label: "Users", icon: Users, testid: "admin-nav-users" },
  { to: "/admin/settings/fee", end: false, label: "Fee settings", icon: Settings, testid: "admin-nav-fee" },
  { to: "/admin/audit", end: false, label: "Audit log", icon: ClipboardList, testid: "admin-nav-audit" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // Preload stats once so the header can show "701 users" etc.
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [loc.pathname]);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-slate-900 text-slate-200 flex flex-col" data-testid="admin-sidebar">
        <div className="px-5 py-6 border-b border-slate-800">
          <FloLogo variant="lockup-dark" size={32} />
          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-2 font-semibold">
            Admin console
          </p>
        </div>
        <nav className="flex-1 py-4 space-y-1">
          {NAV.map(({ to, end, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-slate-800 text-white border-l-2 border-blue-400 font-semibold"
                    : "text-slate-400 hover:text-white hover:bg-slate-800/60"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-800 p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Signed in as</p>
          <p className="text-sm text-slate-200 truncate" data-testid="admin-email">
            {user?.email}
          </p>
          <button
            onClick={() => {
              logout();
              navigate("/");
            }}
            className="mt-3 inline-flex items-center gap-2 text-xs text-slate-400 hover:text-white transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 h-14 flex items-center justify-between px-6">
          <div className="text-sm text-slate-500">
            {stats && (
              <span data-testid="admin-header-summary">
                {stats.users.total} users · {stats.transactions.count} transactions ·{" "}
                {stats.users.by_kyb_status.activated} activated
              </span>
            )}
          </div>
          <span className="text-xs uppercase tracking-wider font-semibold text-slate-400">
            Internal · Flo Payments team only
          </span>
        </header>
        <div className="flex-1 overflow-auto p-6">
          <Outlet context={{ stats }} />
        </div>
      </main>
    </div>
  );
}
